import React, { useEffect, useState } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Plus, PaperPlaneTilt, ChartBar, Users, FileText } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';

const Onboarding = ({ user }) => {
  const [employees, setEmployees] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [employeesRes, contractsRes, dashboardRes] = await Promise.all([
        api.get('/onboarding/employees'),
        api.get('/onboarding/contracts'),
        api.get('/onboarding/dashboard'),
      ]);
      setEmployees(employeesRes.data);
      setContracts(contractsRes.data);
      setDashboardStats(dashboardRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/onboarding/employees', {
        full_name: formData.get('full_name'),
        email: formData.get('email'),
        department: formData.get('department'),
        role: formData.get('role'),
        start_date: formData.get('start_date'),
      });
      toast.success('Employee created');
      setOpenDialog(null);
      loadData();
    } catch (error) {
      toast.error('Failed to create employee');
    }
  };

  const handleCreateContract = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/onboarding/contracts', {
        employee_id: formData.get('employee_id'),
        title: formData.get('title'),
        content: formData.get('content'),
      });
      toast.success('Contract created');
      setOpenDialog(null);
      loadData();
    } catch (error) {
      toast.error('Failed to create contract');
    }
  };

  const handleSendContract = async (contractId) => {
    try {
      await api.put(`/onboarding/contracts/${contractId}/send`);
      toast.success('Contract sent');
      loadData();
    } catch (error) {
      toast.error('Failed to send contract');
    }
  };

  const handleUpdateProgress = async (employeeId, progress) => {
    try {
      await api.put(`/onboarding/employees/${employeeId}`, {
        progress: progress,
        onboarding_status: progress === 100 ? 'completed' : 'in_progress',
      });
      toast.success('Progress updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div data-testid="onboarding-page">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#2B3210] mb-2">
          Employee Onboarding
        </h1>
        <p className="text-base text-[#505631]">Manage employee onboarding and contracts</p>
      </div>

      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList className="mb-6 bg-[#FBF8EF] border border-[#E5E2D9]">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-white" data-testid="tab-dashboard">
            <ChartBar size={16} className="mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="employees" className="data-[state=active]:bg-white" data-testid="tab-employees">
            <Users size={16} className="mr-2" />
            Employees
          </TabsTrigger>
          <TabsTrigger value="contracts" className="data-[state=active]:bg-white" data-testid="tab-contracts">
            <FileText size={16} className="mr-2" />
            Contracts
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Total Employees</p>
              <p className="text-3xl font-bold text-[#2B3210]">{dashboardStats?.total_employees || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">In Onboarding</p>
              <p className="text-3xl font-bold text-blue-600">{dashboardStats?.onboarding_employees || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Active</p>
              <p className="text-3xl font-bold text-green-600">{dashboardStats?.active_employees || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Offboarded</p>
              <p className="text-3xl font-bold text-gray-500">{dashboardStats?.offboarded_employees || 0}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Total Contracts</p>
              <p className="text-2xl font-bold text-[#2B3210]">{dashboardStats?.total_contracts || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Pending Signature</p>
              <p className="text-2xl font-bold text-amber-600">{dashboardStats?.pending_contracts || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Signed Contracts</p>
              <p className="text-2xl font-bold text-green-600">{dashboardStats?.signed_contracts || 0}</p>
            </div>
          </div>
          {/* Departments breakdown */}
          {dashboardStats?.departments && dashboardStats.departments.length > 0 && (
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <h3 className="text-lg font-medium text-[#2B3210] mb-4">Employees by Department</h3>
              <div className="space-y-3">
                {dashboardStats.departments.map((dept, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm text-[#505631]">{dept.name}</span>
                    <span className="text-sm font-medium text-[#2B3210]">{dept.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="employees">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9] flex justify-between items-center">
              <h2 className="text-xl font-medium text-[#2B3210]">Employees</h2>
              <Dialog open={openDialog === 'employee'} onOpenChange={(open) => setOpenDialog(open ? 'employee' : null)}>
                <DialogTrigger asChild>
                  <button className="btn-primary flex items-center gap-2" data-testid="create-employee-button">
                    <Plus size={18} />
                    New Employee
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Onboard New Employee</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateEmployee} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Full Name</label>
                      <input
                        type="text"
                        name="full_name"
                        data-testid="employee-name-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Email</label>
                      <input
                        type="email"
                        name="email"
                        data-testid="employee-email-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Department</label>
                      <input
                        type="text"
                        name="department"
                        data-testid="employee-department-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Role</label>
                      <input
                        type="text"
                        name="role"
                        data-testid="employee-role-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Start Date</label>
                      <input
                        type="date"
                        name="start_date"
                        data-testid="employee-startdate-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      />
                    </div>
                    <button type="submit" className="btn-primary w-full" data-testid="submit-employee-button">
                      Create Employee
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="p-6 space-y-4">
              {employees.map((employee) => (
                <div
                  key={employee.id}
                  data-testid={`employee-card-${employee.id}`}
                  className="border border-[#E5E2D9] rounded-xl p-6"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-[#2B3210]">{employee.full_name}</h3>
                      <p className="text-sm text-[#505631]">
                        {employee.role} • {employee.department}
                      </p>
                      <p className="text-sm text-[#505631]">Start: {employee.start_date}</p>
                    </div>
                    <span className={`status-badge status-${employee.onboarding_status}`}>
                      {employee.onboarding_status}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#505631]">Onboarding Progress</span>
                      <span className="text-[#2B3210] font-medium">{employee.progress}%</span>
                    </div>
                    <Progress value={employee.progress} className="h-2" />
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => handleUpdateProgress(employee.id, Math.min(employee.progress + 25, 100))}
                        data-testid={`update-progress-${employee.id}`}
                        className="btn-secondary text-sm"
                      >
                        Update Progress (+25%)
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="contracts">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9] flex justify-between items-center">
              <h2 className="text-xl font-medium text-[#2B3210]">Contracts</h2>
              <Dialog open={openDialog === 'contract'} onOpenChange={(open) => setOpenDialog(open ? 'contract' : null)}>
                <DialogTrigger asChild>
                  <button className="btn-primary flex items-center gap-2" data-testid="create-contract-button">
                    <Plus size={18} />
                    New Contract
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Generate Contract</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateContract} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Employee</label>
                      <select
                        name="employee_id"
                        data-testid="contract-employee-select"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      >
                        <option value="">Select employee</option>
                        {employees.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.full_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Contract Title</label>
                      <input
                        type="text"
                        name="title"
                        data-testid="contract-title-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Contract Content</label>
                      <textarea
                        name="content"
                        data-testid="contract-content-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        rows="6"
                        required
                      />
                    </div>
                    <button type="submit" className="btn-primary w-full" data-testid="submit-contract-button">
                      Generate Contract
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FBF8EF] border-b border-[#E5E2D9]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Employee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {contracts.map((contract) => {
                    const employee = employees.find((e) => e.id === contract.employee_id);
                    return (
                      <tr key={contract.id} data-testid={`contract-row-${contract.id}`}>
                        <td className="px-6 py-4 text-sm font-medium text-[#2B3210]">{contract.title}</td>
                        <td className="px-6 py-4 text-sm text-[#2B3210]">
                          {employee?.full_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`status-badge status-${contract.status === 'sent' ? 'pending' : 'in_progress'}`}>
                            {contract.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {contract.status === 'draft' && (
                            <button
                              onClick={() => handleSendContract(contract.id)}
                              data-testid={`send-contract-${contract.id}`}
                              className="flex items-center gap-2 px-3 py-2 text-sm bg-[#B2503A] text-white rounded-lg hover:bg-[#9A422F] transition-colors"
                            >
                              <PaperPlaneTilt size={16} />
                              Send
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Onboarding;
